/**
 * El plan de reescritura (Fase 12, R-15).
 *
 * Con los informes hechos, el editor prepara el plan: áreas, pasadas con sus capítulos y su criterio
 * de cierre, y lo que no se toca. Nace sin aprobar; aprobarlo es del autor y queda en la bitácora.
 * Con un motor anterior a la 1.6.3 no hay ruta: el plan se pide por el chat del editor, y se dice.
 */
import { useEffect, useState } from "react";
import { actualizarPlan, editarPasada, estimacionDelPlan, preferenciasDelLibro, prepararPlan } from "../api";
import type { CambioPasada, PasadaPlan, PlanLibro, Trabajo } from "../api";
import type { Caducidad } from "../logica";
import AvisoCaduca from "./AvisoCaduca";
import { frasesDelTablero, nombreDeRuta, notaDePasada, resumenDePreferencias, resumenDelPlan, esperaDelPlan, porcentajeDe, loQueTardaElPlan } from "../logica";
import type { PreferenciasEdicion } from "../logica";
import { useFlujo } from "../flujo";

const capitulos = (c?: number[] | string) =>
  !c ? "" : Array.isArray(c) ? (c.length > 2 && c.every((x, i) => i === 0 || x === c[i - 1] + 1) ? `${c[0]}–${c[c.length - 1]}` : c.join(", ")) : c;

export default function Plan({ plan, soportado, planEnCarpeta, carpeta, doc, trabajo, pasadaEnMarcha = false, alLanzar, alCambio, alHablar, encargo, faltan = null, alIr, caduca = null, faltanEscenas = false }: {
  plan: PlanLibro | null;
  soportado: boolean;
  planEnCarpeta: boolean;
  carpeta: string;
  doc: string | null;
  trabajo: Trabajo | null;
  pasadaEnMarcha?: boolean;
  alLanzar: () => void;
  alCambio: () => void;
  alHablar: (agente: string, encargo: string) => void;
  encargo: string;
  /** Los informes que faltan para poder pedirlo (motor 1.6.8); `null` si el motor no lo dice. */
  faltan?: { id: string; titulo: string }[] | null;
  alIr?: (seccion: string) => void;
  /** Si los informes con que se escribe el plan son de antes de un cambio que los dejó viejos. */
  caduca?: Caducidad | null;
  /** En el flujo: el libro todavía no está dividido en escenas, y el plan se escribe sobre ellas (M-3). */
  faltanEscenas?: boolean;
}) {
  const [objetivo, setObjetivo] = useState("");
  // Las tres preguntas de la skill del plan (25-sep): las zonas intocables «se preguntan o no existen».
  const [zonasNuevas, setZonasNuevas] = useState("");
  const [alcance, setAlcance] = useState("Poda y estructura");
  const [zonas, setZonas] = useState<string | null>(null);   // null = no se está editando
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  // 17-sep: el plan deja de ser todo o nada. Qué pasada se está editando (n), o «nueva».
  const [editando, setEditando] = useState<number | "nueva" | null>(null);
  // EL STUDIO COMO FLUJO (3-oct-2026): el plan es una lista de casillas que se aprueba de golpe.
  const flujo = useFlujo();
  // Lo que el autor deja hacer con su texto (M-1): lo dice su perfil y el motor lo aplica al plan. Aquí
  // se enseña para que se sepa con qué se va a trabajar. `null`: un motor que todavía no lo tiene.
  const [prefs, setPrefs] = useState<PreferenciasEdicion | null>(null);
  useEffect(() => {
    if (!flujo || !carpeta) return;
    preferenciasDelLibro(carpeta, doc).then(setPrefs).catch(() => setPrefs(null));
  }, [flujo, carpeta, doc, plan]);
  const lineaDePerfil = !prefs ? null : (
    <p className="vacio">
      <b>Tu perfil{prefs.autor ? ` (${prefs.autor.nombre})` : ""}:</b>{" "}
      {prefs.autor ? resumenDePreferencias(prefs) : "este libro no tiene firma, así que no se aplica ninguna preferencia tuya. Elige su firma en Proyectos."}{" "}
      {alIr && <button className="btn texto" onClick={() => alIr(prefs.autor ? "autores" : "proyectos")}>{prefs.autor ? "Cambiarlo en Autores" : "Ir a Proyectos"}</button>}
    </p>
  );
  const enCurso = trabajo?.estado === "en_curso";
  // Cuánto va a tardar, para decirlo ANTES de pulsar (Rais, 4-oct). Solo cuando se ofrece prepararlo;
  // un motor que no lo estima contesta sin la cifra y no se dice nada.
  const [estimacion, setEstimacion] = useState<{ minutos: number } | null>(null);
  const seOfrece = !plan && soportado && !enCurso && !(faltan && faltan.length > 0);
  useEffect(() => {
    setEstimacion(null);
    if (!seOfrece || !carpeta || !doc) return;
    let vivo = true;
    estimacionDelPlan(carpeta, doc).then((e) => { if (vivo) setEstimacion(e); }).catch(() => { /* sin estimación: no se inventa */ });
    return () => { vivo = false; };
  }, [seOfrece, carpeta, doc]);
  // Cuánto lleva: «está trabajando» sin más, en algo que tarda minutos, se lee como colgado.
  const desdeHace = (() => {
    if (!enCurso || !trabajo?.inicio) return "";
    const min = Math.max(0, Math.round((Date.now() - Date.parse(trabajo.inicio)) / 60000));
    const hora = new Date(trabajo.inicio).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    return ` Empezó a las ${hora}${min >= 1 ? `, lleva ${min} ${min === 1 ? "minuto" : "minutos"}` : ""}.`;
  })();
  const pasadas = (plan?.areas ?? []).flatMap((a) => (a.pasadas ?? []).map((p) => ({ ...p, area: a.nombre })));
  const siguiente = pasadas.find((p) => p.estado !== "hecha" && p.estado !== "saltada");
  const vivas = pasadas.filter((p) => p.estado !== "hecha");

  async function preparar(rehacer = false) {
    if (!doc) return;
    setOcupado(true); setFallo(null);
    try {
      await prepararPlan(carpeta, doc, objetivo, rehacer,
        { zonas: zonasNuevas.split("\n").map((z) => z.trim()).filter(Boolean), alcance, libre: flujo });
      alLanzar();
    }
    catch (e) {
      setFallo((e as Error).message);
      // Si el motor dice que hay algo en marcha, se vuelve a preguntar por los trabajos: así la
      // pantalla pasa a «el editor está trabajando» en vez de quedarse con una línea roja fija
      // (Rais, 22-sep: «dice que hay una pasada, pero no la hay y no avanza»).
      alLanzar();
    }
    finally { setOcupado(false); }
  }
  async function tocar(c: CambioPasada) {
    setOcupado(true); setFallo(null);
    try { await editarPasada(carpeta, c, doc); setEditando(null); alCambio(); } catch (e) { setFallo((e as Error).message); } finally { setOcupado(false); }
  }
  async function cambiar(c: { aprobado?: boolean; zonas_intocables?: string[] }) {
    setOcupado(true); setFallo(null);
    try { await actualizarPlan(carpeta, c, doc); setZonas(null); alCambio(); } catch (e) { setFallo((e as Error).message); } finally { setOcupado(false); }
  }
  /** Marcar o desmarcar una fase entera: «poda no quiero, pum, la quito» (Rais, 3-oct). Una a una,
   *  porque el motor de hoy salta o recupera pasada a pasada. */
  async function tocarFase(ns: number[], accion: "saltar" | "recuperar") {
    setOcupado(true); setFallo(null);
    try { for (const n of ns) await editarPasada(carpeta, { accion, n }, doc); alCambio(); }
    catch (e) { setFallo((e as Error).message); alCambio(); }
    finally { setOcupado(false); }
  }
  async function aprobarYEmpezar() {
    setOcupado(true); setFallo(null);
    try { await actualizarPlan(carpeta, { aprobado: true }, doc); alCambio(); alIr?.("pasadas"); }
    catch (e) { setFallo((e as Error).message); }
    finally { setOcupado(false); }
  }

  if (!soportado) {
    return (
      <section className="carta">
        <h2>Siguiente paso</h2>
        <p className="vacio">
          {flujo
            ? "Tu motor es anterior al que prepara el plan aquí. Actualízalo en Ajustes y vuelve: el plan se prepara, se ve y se aprueba en esta pantalla."
            : planEnCarpeta
            ? "Tienes un plan de reescritura en la carpeta. Pídele al editor que lo repase contigo antes de las pasadas."
            : "El editor prepara el plan a partir de tus informes. Con el motor 1.6.3 se prepara, se ve y se aprueba aquí; con el tuyo, se pide en el chat."}
        </p>
        {!flujo && <div className="botones"><button className="btn" onClick={() => alHablar("editor", encargo)}>Pedírselo al editor</button></div>}
      </section>
    );
  }

  if (!plan) {
    return (
      <section className="carta">
        <h2>{flujo ? "2 · " : ""}Preparar el plan<span className="sub">{enCurso ? (flujo ? "preparándolo" : "el editor está trabajando") : "sin plan todavía"}</span></h2>
        {enCurso ? (
          // Prueba real del 4-oct: el plan de un libro entero tardó diez minutos y medio, siete de ellos
          // pensando antes de escribir la primera palabra. Se dice solo con el motor que no lo corta (`esperaDelPlan`).
          <>
            {/* La barra, solo si el motor MIDE el avance (Rais, 4-oct): cuenta el tiempo mientras piensa y lo escrito cuando escribe. */}
            {porcentajeDe(trabajo) != null && (
              <div className="barra-progreso" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={porcentajeDe(trabajo) ?? 0} aria-label="Avance del plan">
                <i style={{ width: `${Math.max(3, porcentajeDe(trabajo) ?? 0)}%` }} />
              </div>
            )}
            <p className="vacio">
              {flujo ? "Se está leyendo el libro con su diagnóstico." : "El editor está leyendo el libro y los informes."}{desdeHace}{" "}
              {esperaDelPlan(trabajo)}{esperaDelPlan(trabajo) ? " " : ""}Puedes cerrar el Studio: el plan aparece aquí al terminar.
            </p>
          </>
        ) : faltan && faltan.length > 0 ? (
          // Rais, 21-sep: «el plan de reescritura se tiene que generar una vez tenemos todos los informes».
          <div className="formulario">
            <p className="vacio">
              El plan se escribe con todo el diagnóstico delante: si falta una lectura, {flujo ? "se decide" : "el editor decide"} la reescritura con esa parte
              a ciegas. Te falta:
            </p>
            <ul className="vacio" style={{ margin: "4px 0 8px 18px" }}>
              {faltan.map((f) => <li key={f.id}><b>{f.titulo}</b></li>)}
            </ul>
            {alIr && <div className="botones"><button className="btn" onClick={() => alIr("diagnostico")}>{flujo ? "Completar el diagnóstico" : "Pedirlos en Diagnóstico"}</button></div>}
          </div>
        ) : (
          <div className="formulario">
            <p className="vacio">
              {flujo
                ? "Se lee el manuscrito entero con su diagnóstico y se proponen las fases y las pasadas en orden, con sus capítulos y cuándo se da cada una por cerrada. Tú decides cuáles se hacen y lo apruebas."
                : "El editor lee el manuscrito entero y los informes del Diagnóstico, reconcilia lo que dicen y propone las pasadas en orden, con sus capítulos y cuándo se da cada una por cerrada. Tú lo apruebas."}
            </p>
            {planEnCarpeta && (
              <p className="aviso">
                En tu carpeta hay un plan de reescritura, pero el Studio no lo tiene cargado (puede pasar si el motor
                se reinició al actualizar). Recarga la página para recuperarlo; si sigue sin salir, vuelve a hacerlo aquí:
                el que hay en la carpeta no se borra.
              </p>
            )}
            <label className="campo">
              <span>Lo que no se toca <i>(una cosa por línea; {flujo ? "no se deduce" : "el editor nunca lo deduce"}: si no escribes nada, no hay)</i></span>
              <div className="dir">
                <textarea className="escribir" rows={3} style={{ resize: "vertical" }} value={zonasNuevas} onChange={(e) => setZonasNuevas(e.target.value)}
                          placeholder={"Por ejemplo:\nel prólogo\nla carta de Oksana en el capítulo 12"} />
              </div>
            </label>
            <label className="campo">
              <span>Objetivo de extensión y lo que quieres conseguir <i>(si no lo dices, te propone la horquilla de tu género)</i></span>
              <div className="dir">
                <textarea className="escribir" rows={2} style={{ resize: "vertical" }} value={objetivo} onChange={(e) => setObjetivo(e.target.value)}
                          placeholder="Por ejemplo: llegar a 90.000 palabras; que la parte 2 no se atasque." />
              </div>
            </label>
            <label className="campo">
              <span>Hasta dónde llega</span>
              <select className="escribir" value={alcance} onChange={(e) => setAlcance(e.target.value)}>
                <option>Poda y estructura</option>
                <option>Poda, estructura y escenas nuevas</option>
              </select>
            </label>
            {flujo && lineaDePerfil}
            <div className="pie-form">
              <p className="vacio">{faltanEscenas ? "Antes, divide el libro en escenas (arriba): el plan se escribe sobre ellas."
                : trabajo?.estado === "fallido" ? `El último intento falló: ${trabajo.error}` : (flujo ? "Se paga con tu llave." : "Se paga con tu llave y lo hace el mejor modelo del editor.")}
                {!faltanEscenas && loQueTardaElPlan(estimacion) ? ` ${loQueTardaElPlan(estimacion)}` : ""}</p>
              <button className="btn" disabled={!doc || ocupado || pasadaEnMarcha || faltanEscenas} onClick={() => preparar(planEnCarpeta)}>
                {planEnCarpeta ? "Rehacer el plan" : "Preparar el plan"}
              </button>
            </div>
          </div>
        )}
        {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
      </section>
    );
  }

  if (flujo) {
    const r = resumenDelPlan(plan);
    const zonasLimpias = (plan.zonas_intocables ?? []).filter((z) => !/pendientes? de declarar|por declarar|sin declarar|ninguna declarada/i.test(z));
    return (
      <section className="carta">
        <h2>Plan de reescritura
          <span className="sub">{plan.aprobado ? `aprobado el ${plan.fecha_aprobado ?? "—"}` : "sin aprobar"} · {r.fases} {r.fases === 1 ? "fase" : "fases"} · {r.total} pasadas</span>
        </h2>
        {caduca && <AvisoCaduca c={caduca} />}
        {plan.recortado && <p className="vacio">Tu manuscrito es más largo de lo que se puede leer de una vez: el plan se hizo con las primeras ~83.000 palabras.</p>}
        <p className="vacio">
          Marca lo que quieres que se haga. Lo que desmarques no se ejecuta. El plan entero, con su porqué, está en Word en la
          carpeta de tu libro{plan.documento ? <> (<b className="mono">{nombreDeRuta(plan.documento)}</b>)</> : null}.
        </p>

        <div className="lista-etapa">
          {(plan.areas ?? []).map((a, i) => {
            const vivasDeFase = (a.pasadas ?? []).filter((p) => p.estado !== "hecha");
            const marcadas = vivasDeFase.filter((p) => p.estado !== "saltada");
            const todasHechas = (a.pasadas ?? []).length > 0 && vivasDeFase.length === 0;
            const hechasDeFase = (a.pasadas ?? []).length - vivasDeFase.length;
            // Las que el perfil del autor no deja hacer no se pueden marcar: ni una a una ni con la fase.
            const marcables = vivasDeFase.filter((p) => !notaDePasada(p).bloqueada);
            return (
              <div key={a.nombre} className="fase-plan">
                <label className={`fila-etapa casilla fase${marcadas.length === 0 && hechasDeFase === 0 ? " fuera" : ""}`}>
                  <input type="checkbox" checked={todasHechas || marcadas.length > 0} disabled={ocupado || todasHechas || marcables.length === 0}
                         aria-label={`Fase ${i + 1}: ${a.nombre}`}
                         onChange={() => tocarFase((marcadas.length ? marcadas : marcables).map((p) => p.n), marcadas.length ? "saltar" : "recuperar")} />
                  <div className="que"><b>Fase {i + 1} · {a.nombre}</b>
                    <span className="vacio">{todasHechas ? "hecha" : [
                        marcadas.length ? `${marcadas.length} por hacer` : "",
                        hechasDeFase ? `${hechasDeFase} ${hechasDeFase === 1 ? "hecha" : "hechas"}` : "",
                        vivasDeFase.length - marcadas.length ? `${vivasDeFase.length - marcadas.length} no se ${vivasDeFase.length - marcadas.length === 1 ? "hace" : "hacen"}` : "",
                      ].filter(Boolean).join(" · ")}{a.puerta ? ` · al acabar: ${a.puerta}` : ""}</span>
                  </div>
                </label>
                {(a.pasadas ?? []).map((p) => {
                  const perfil = notaDePasada(p);
                  return (
                  <label className={`fila-etapa casilla pasada-plan${p.estado === "saltada" ? " fuera" : ""}`} key={p.n}>
                    <input type="checkbox" checked={p.estado !== "saltada"} disabled={ocupado || p.estado === "hecha" || perfil.bloqueada}
                           aria-label={`Pasada ${p.n}: ${p.titulo}`}
                           onChange={() => tocar({ accion: p.estado === "saltada" ? "recuperar" : "saltar", n: p.n })} />
                    <div className="que">
                      <b>{p.titulo}{p.propia ? <span className="vacio"> · tuya</span> : null}</b>
                      {p.porque && <span className="vacio">{p.porque}</span>}
                      <span className="vacio">
                        {[
                          capitulos(p.capitulos) ? `${Array.isArray(p.capitulos) && p.capitulos.length === 1 ? "capítulo" : "capítulos"} ${capitulos(p.capitulos)}` : "",
                          p.delta_esperado ? `${p.delta_esperado > 0 ? "+" : ""}${p.delta_esperado.toLocaleString("es-ES")} palabras` : "",
                          p.criterio_cierre ? `se cierra cuando ${p.criterio_cierre}` : "",
                        ].filter(Boolean).join(" · ")}
                      </span>
                      {perfil.texto && <span className="vacio" style={perfil.bloqueada ? { color: "var(--acc)" } : undefined}>{perfil.texto}</span>}
                    </div>
                    {p.estado !== "hecha" && p.estado !== "saltada" && p.prioridad && <span className="chip-estado" title="Lo que más cambia el libro por lo que cuesta hacerlo">prioridad {p.prioridad}</span>}
                    {p.estado === "hecha" && <span className="chip-estado ok">hecha{p.build ? ` · ${p.build}` : ""}</span>}
                    {p.estado === "saltada" && <span className="chip-estado">no se hace</span>}
                  </label>
                  );
                })}
              </div>
            );
          })}
        </div>

        {lineaDePerfil}
        <p className="vacio"><b>No se toca{prefs?.intocables.length ? " en este libro" : ""}:</b> {zonasLimpias.length ? zonasLimpias.join(" · ") : "nada marcado."}{" "}
          {zonas === null && <button className="btn texto" disabled={ocupado} onClick={() => setZonas((plan.zonas_intocables ?? []).join("\n"))}>Cambiarlo</button>}
        </p>
        {zonas !== null && (
          <div className="formulario" style={{ maxWidth: "none" }}>
            <div className="dir"><textarea className="escribir" rows={3} value={zonas} onChange={(e) => setZonas(e.target.value)} placeholder="Una cosa por línea: el prólogo, el capítulo 22…" /></div>
            <div className="botones">
              <button className="btn fantasma" disabled={ocupado} onClick={() => cambiar({ zonas_intocables: zonas.split("\n").map((z) => z.trim()).filter(Boolean) })}>Guardar</button>
              <button className="btn texto" onClick={() => setZonas(null)}>Cancelar</button>
            </div>
          </div>
        )}

        {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
        <div className="pie-form">
          <p className="vacio">
            {r.pendientes === 0 && r.hechas > 0 ? "Todas las pasadas marcadas están hechas."
              : r.pendientes === 0 ? "No hay ninguna pasada marcada: marca al menos una para poder empezar."
              : `Se ${r.pendientes === 1 ? "ejecutará 1 pasada" : `ejecutarán ${r.pendientes} pasadas`}${r.fuera ? `; ${r.fuera === 1 ? "1 no se hace" : `${r.fuera} no se hacen`}` : ""}${r.hechas ? `; ${r.hechas === 1 ? "1 ya está hecha" : `${r.hechas} ya están hechas`}` : ""}.`}
          </p>
          {plan.aprobado
            ? <button className="btn" disabled={ocupado} onClick={() => alIr?.("pasadas")}>Ir a las pasadas</button>
            : <button className="btn" disabled={ocupado || r.pendientes === 0} onClick={aprobarYEmpezar}>Aprobar el plan y empezar</button>}
        </div>
        <div className="botones">
          {plan.aprobado && <button className="btn texto" disabled={ocupado} onClick={() => cambiar({ aprobado: false })}>Quitar la aprobación</button>}
          <button className="btn texto" disabled={ocupado || enCurso || pasadaEnMarcha || !doc} onClick={() => preparar(true)}
                  title={pasadaEnMarcha ? "Hay una pasada en marcha: espera a que termine" : undefined}>{enCurso ? "Rehaciendo…" : "Rehacer el plan"}</button>
        </div>
      </section>
    );
  }

  return (
    <section className="carta">
      <h2>Plan de reescritura
        <span className="sub">{plan.aprobado ? `aprobado el ${plan.fecha_aprobado ?? "—"}` : "sin aprobar"} · {pasadas.length} pasadas{plan.documento ? ` · ${nombreDeRuta(plan.documento)}` : ""}</span>
      </h2>
      {caduca && <AvisoCaduca c={caduca} />}
      {plan.recortado && <p className="vacio">El manuscrito es más largo de lo que el editor puede leer de una vez: el plan se hizo con las primeras ~83.000 palabras.</p>}
      {!plan.aprobado && (
        <div className="aviso">
          Léelo (está también en tu carpeta como documento) y apruébalo antes de empezar las pasadas. Puedes cambiar el orden,
          saltar o editar pasadas y añadir las tuyas antes o después de aprobarlo.
          <button className="btn" disabled={ocupado} onClick={() => cambiar({ aprobado: true })}>Aprobar el plan</button>
        </div>
      )}

      <div className="lista-etapa">
        {(plan.areas ?? []).map((a) => (
          <div key={a.nombre}>
            <p className="vacio" style={{ marginTop: 10, fontWeight: 600 }}>{a.nombre}</p>
            {(a.pasadas ?? []).map((p) => editando === p.n ? (
              <FormPasada key={p.n} inicial={p} ocupado={ocupado} alCancelar={() => setEditando(null)}
                          alGuardar={(c) => tocar({ accion: "editar", n: p.n, ...c })} />
            ) : (
              <div className={`fila-etapa pasada ${p.estado === "hecha" ? "hecha" : p.estado === "saltada" ? "saltada" : p.n === siguiente?.n ? "toca" : ""}`} key={p.n}>
                <span className="num">{p.n}</span>
                <div className="que">
                  <b>{p.titulo}{p.propia ? <span className="vacio"> · tuya</span> : p.editada ? <span className="vacio"> · retocada</span> : null}</b>
                  <span className="vacio">
                    {capitulos(p.capitulos) ? `${Array.isArray(p.capitulos) && p.capitulos.length === 1 ? "capítulo" : "capítulos"} ${capitulos(p.capitulos)}` : ""}
                    {p.delta_esperado ? ` · ${p.delta_esperado > 0 ? "+" : ""}${p.delta_esperado.toLocaleString("es-ES")} palabras` : ""}
                    {p.criterio_cierre ? ` · se cierra cuando ${p.criterio_cierre}` : ""}
                  </span>
                  {/* R0-6: el plan y el tablero son lo mismo: lo que escribió la pasada está en las notas. */}
                  {p.estado === "hecha" && frasesDelTablero(p) && <span className="vacio">{frasesDelTablero(p)}</span>}
                  {/* Motor 1.7: lo que el perfil del autor dice de esta pasada (equipo rojo, 3-oct: salía
                      «saltada» con «Recuperar» y sin explicación hasta que el motor contestaba que no). */}
                  {notaDePasada(p).texto && <span className="vacio" style={notaDePasada(p).bloqueada ? { color: "var(--acc)" } : undefined}>{notaDePasada(p).texto}</span>}
                  {p.estado !== "hecha" && (
                    <span className="acciones-pasada">
                      <button className="chip" disabled={ocupado || vivas[0]?.n === p.n} onClick={() => tocar({ accion: "mover", n: p.n, hacia: -1 })} aria-label={`Subir la pasada ${p.n}`}>↑</button>
                      <button className="chip" disabled={ocupado || vivas.at(-1)?.n === p.n} onClick={() => tocar({ accion: "mover", n: p.n, hacia: 1 })} aria-label={`Bajar la pasada ${p.n}`}>↓</button>
                      {!notaDePasada(p).bloqueada && (
                        <button className="chip" disabled={ocupado} onClick={() => tocar({ accion: p.estado === "saltada" ? "recuperar" : "saltar", n: p.n })}>
                          {p.estado === "saltada" ? "Recuperar" : "Saltar"}
                        </button>
                      )}
                      <button className="chip" disabled={ocupado} onClick={() => setEditando(p.n)}>Editar</button>
                      {p.propia && <button className="chip" disabled={ocupado} onClick={() => tocar({ accion: "quitar", n: p.n })}>Quitar</button>}
                    </span>
                  )}
                </div>
                {p.estado === "hecha" ? <span className="chip-estado ok">hecha{p.build ? ` · ${p.build}` : ""}</span>
                  : p.estado === "saltada" ? <span className="chip-estado">saltada</span>
                  : p.n === siguiente?.n ? <span className="chip-estado acc">toca ahora</span> : <span className="chip-estado">pendiente</span>}
              </div>
            ))}
            {a.puerta && <p className="vacio">Puerta al acabar el área: {a.puerta}</p>}
          </div>
        ))}
      </div>

      {editando === "nueva"
        ? <FormPasada ocupado={ocupado} alCancelar={() => setEditando(null)} alGuardar={(c) => tocar({ accion: "anadir", titulo: c.titulo ?? "", capitulos: c.capitulos ?? "", criterio_cierre: c.criterio_cierre })} />
        : <div className="botones"><button className="btn fantasma" disabled={ocupado} onClick={() => setEditando("nueva")}>Añadir una pasada tuya</button></div>}

      <div className="formulario" style={{ maxWidth: "none" }}>
        {(() => {
          // «PENDIENTE DE DECLARAR…» no es una zona: es el editor diciendo que te lo iba a preguntar.
          const zonas = (plan.zonas_intocables ?? []).filter((z) => !/pendientes? de declarar|por declarar|sin declarar|ninguna declarada/i.test(z));
          return (
            <p className="vacio"><b>No se toca:</b> {zonas.length ? zonas.join(" · ") : "nada marcado todavía."}
              {zonas.length > 0 && !plan.zonas_autor && <> <i>(las escribió el editor en este plan, no tú: revísalas y quita las que no sean tuyas)</i></>}
            </p>
          );
        })()}
        {(() => {
          const propuestas = (plan.zonas_propuestas ?? []).filter((z) => !(plan.zonas_intocables ?? []).includes(z));
          return propuestas.length > 0 && (
            <p className="vacio"><b>El editor propone además no tocar:</b> {propuestas.join(" · ")}.{" "}
              <button className="btn texto" disabled={ocupado} onClick={() => cambiar({ zonas_intocables: [...(plan.zonas_intocables ?? []), ...propuestas] })}>Marcarlas también</button>
            </p>
          );
        })()}
        {zonas !== null && (
          <div className="dir">
            <textarea className="escribir" rows={3} value={zonas} onChange={(e) => setZonas(e.target.value)} placeholder="Una zona por línea: el prólogo, el capítulo 22…" />
          </div>
        )}
        <div className="botones">
          {zonas === null
            ? <button className="btn fantasma" onClick={() => setZonas((plan.zonas_intocables ?? []).join("\n"))}>Marcar lo que no se toca</button>
            : <button className="btn" disabled={ocupado} onClick={() => cambiar({ zonas_intocables: zonas.split("\n").map((z) => z.trim()).filter(Boolean) })}>Guardar</button>}
          {plan.aprobado && <button className="btn texto" disabled={ocupado} onClick={() => cambiar({ aprobado: false })}>Quitar la aprobación</button>}
          <button className="btn texto" disabled={ocupado || enCurso || pasadaEnMarcha || !doc} onClick={() => preparar(true)}
                  title={pasadaEnMarcha ? "Hay una pasada en marcha: espera a que termine" : undefined}>{enCurso ? "Rehaciendo…" : "Rehacer el plan"}</button>
        </div>
      </div>
      {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
    </section>
  );
}

/** Editar una pasada o añadir una: título, capítulos y cuándo se da por cerrada. */
function FormPasada({ inicial, ocupado, alGuardar, alCancelar }: {
  inicial?: PasadaPlan; ocupado: boolean;
  alGuardar: (c: { titulo?: string; capitulos?: string; criterio_cierre?: string }) => void; alCancelar: () => void;
}) {
  const [titulo, setTitulo] = useState(inicial?.titulo ?? "");
  const [caps, setCaps] = useState(Array.isArray(inicial?.capitulos) ? inicial!.capitulos.join(", ") : String(inicial?.capitulos ?? ""));
  const [criterio, setCriterio] = useState(inicial?.criterio_cierre ?? "");
  return (
    <div className="formulario form-pasada">
      <label className="campo"><span>{inicial ? `Pasada ${inicial.n}` : "Tu pasada"}</span>
        <div className="dir"><input className="escribir" value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Qué se trabaja: por ejemplo, quitar explicaciones en los diálogos" /></div></label>
      <label className="campo"><span>Capítulos</span>
        <div className="dir"><input className="escribir" value={caps} onChange={(e) => setCaps(e.target.value)} placeholder="3, 4, 5" /></div></label>
      <label className="campo"><span>Se da por cerrada cuando <i>(opcional)</i></span>
        <div className="dir"><input className="escribir" value={criterio} onChange={(e) => setCriterio(e.target.value)} placeholder="ningún diálogo explica lo que ya se ve" /></div></label>
      <div className="botones">
        <button className="btn" disabled={ocupado || !titulo.trim() || !caps.trim()} onClick={() => alGuardar({ titulo, capitulos: caps, criterio_cierre: criterio })}>Guardar</button>
        <button className="btn texto" onClick={alCancelar}>Cancelar</button>
      </div>
    </div>
  );
}
